/**
 * Globals injected at build time (rollup.react.config.js).
 */
declare global {
  const __API_BASE_URL__: string;
  const __SDK_SELF_ORIGIN__: boolean;
}

// currentScript is set only while a classic script (the loader) executes, and
// a module (the core) has none and reads its own URL from import.meta.url.
// Both hold only at module top level, so this never runs lazily.
function scriptOrigin(): string {
  try {
    const src =
      (document.currentScript as HTMLScriptElement | null)?.src ||
      import.meta.url;
    return src ? new URL(src).origin : '';
  } catch {
    return '';
  }
}

/**
 * Hosted-app origin that every request and redirect targets.
 *
 * Local builds follow whichever origin served the bundle (http `next start`,
 * https `next dev`, any port). Hosted builds stay pinned to their environment
 * so a copy re-hosted by a WordPress optimizer still talks to us.
 */
export const API_BASE_URL: string =
  (__SDK_SELF_ORIGIN__ && scriptOrigin()) || __API_BASE_URL__;

/**
 * SDK API contract version, sent on every request as the `sdkVersion` query param.
 *
 * This is the version of the request/response CONTRACT, not the npm package. It lets the
 * server serve the right response shape per embed: when an endpoint's shape changes
 * (e.g. the Phase 1 list-view merge), bump this and have the server branch on it, while
 * still serving the legacy shape to embeds that send an older version (or none; embeds
 * built before this was introduced send nothing and are treated as the legacy baseline).
 *
 * Sent as a query param (not a header) on purpose: a custom request header would trigger
 * a CORS preflight on every request and require allowlisting it server-side.
 *
 * QA-TBD: 1.1.0 reads classes by id; the bump splits its cache key from 1.0.0.
 */
export const SDK_API_VERSION = '1.1.0';

/**
 * Constructs a full URL by combining the base URL with the provided path
 * @param path - The API endpoint path
 * @returns The full URL
 */
export function getFullUrl(path: string): string {
  if (path.startsWith('http')) {
    return path;
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}
